import React, { useCallback, useMemo, useState, useRef } from 'react';
import { useSelector } from 'react-redux';
import { Box, Button, OptionType } from '@fullstory/components';
import {
  namedElementsSelectors,
  watchedElementsSelectors,
  getTopUnmappedSpecObjects,
  TargetType,
  useFetchFsSpec,
  allSpecElementsBySlug,
} from '@fullstory/redux';
import {
  Metadata,
  ResourceSelectMenu,
  getSelectedItemLabel,
  UNMAPPED_SPEC_THRESHOLD,
} from '@fullstory/library-ui';
import { useFlags } from '@fullstory/react-hooks';
import { Flag } from '@fullstory/proto/fs/shared/flags';
import { toDxObjectsMetadata } from '@fullstory/util/auditmetadata';
import { NamedElementBody } from '../NamedElementBody';
import { NamedElementModalSource } from '../NamedElementModal';
import { useNamedElementModal } from '../../containers/NamedElementModalContainer';
import { getNamedElementMetadata, placeholderNamedElementRecord } from '../../utils';
import { FormattedMessage, useIntl } from 'react-intl';
import { intl } from '@fullstory/intl';
import { ElementValue, ElementValueType } from '@fullstory/search-types';
import { isEqual, partition } from 'lodash';
import {
  NamedElement,
  NamedElementRecord,
  Suggestion,
} from '@fullstory/proto/fs/services/search-settings';
import { isNonNull } from '@fullstory/typeutils';
import { SpecElementBody } from '../SpecElementBody';
import { Trie } from '@fullstory/util/trie';

const { getAllSuggestedElementsById, getNamedElementsById, getNamedElementsTriePromise } =
  namedElementsSelectors;
const { getWatchedNamedElementIds } = watchedElementsSelectors;

const allElementsGroup = intl.formatMessage({ defaultMessage: 'All Elements', id: 'oG8jmq' });
const aiSuggestionsGroup = intl.formatMessage({ defaultMessage: 'AI Suggestions', id: 't3n6rd' });
const initialSuggestionsLimit = 3;
const moreSuggestionsKey = '__more-suggestions__';
const emptyMap = new Map();

interface NamedElementSelectMenuProps
  extends Pick<
    React.ComponentProps<typeof ResourceSelectMenu>,
    | 'variant'
    | 'fullWidth'
    | 'compact'
    | 'size'
    | 'placeholder'
    | 'initialOpen'
    | 'onClose'
    | 'disabled'
    | 'flipY'
    | 'preventOverflow'
  > {
  namedElement: ElementValue | null;
  setNamedElement: (namedElement: ElementValue | null) => void;
  source: NamedElementModalSource;
  omitSpecElements?: boolean;
  seen?: boolean;
  clearable?: boolean;
  onCreate?: React.ComponentProps<typeof ResourceSelectMenu>['onCreate'];
  promote?: PromoteProps;
  omitNonPromoted?: boolean;
  filter?: Set<string>;
  includeSuggestions?: boolean;
}

interface PromoteProps {
  group: string;
  ids: Set<string>;
}

const CREATE_NEW_KEY = 'create-new';

export const NamedElementSelectMenu = ({
  namedElement,
  setNamedElement,
  variant,
  fullWidth,
  compact,
  size,
  placeholder = intl.formatMessage({ defaultMessage: 'Select Named Element...', id: '/CFeT2' }),
  initialOpen,
  onClose: _onClose,
  source,
  omitSpecElements,
  disabled = false,
  seen = false,
  clearable,
  onCreate,
  flipY,
  preventOverflow,
  promote,
  omitNonPromoted,
  filter,
  includeSuggestions,
}: NamedElementSelectMenuProps) => {
  const flags = useFlags();
  const intl = useIntl();

  const [pendingSpecSlug, setPendingSpecSlug] = useState<string>();
  const { setModalInput, getNamedElementModalComponent } = useNamedElementModal(source);

  const namedElementsById = useSelector(getNamedElementsById);
  const watchedNamedElementIds = useSelector(getWatchedNamedElementIds);

  const _suggestedElementsById = useSelector(getAllSuggestedElementsById);
  const suggestedElementsById = includeSuggestions ? _suggestedElementsById : emptyMap;
  const [suggestedElementsLimited, setSuggestedElementsLimited] = useState(true);
  const previousFilterTextRef = useRef<string>('');

  const onClose = useCallback(() => {
    setSuggestedElementsLimited(true);
    _onClose?.();
  }, [_onClose]);
  const closeOnSelect = useCallback(
    (option: OptionType<ElementValue> | null): boolean =>
      // more suggestions option is a special case that should not close the menu
      option?.key !== moreSuggestionsKey,
    [],
  );

  useFetchFsSpec();
  const specElementsBySlug = useSelector(allSpecElementsBySlug);
  const topUnmappedSpecObjects = useSelector(getTopUnmappedSpecObjects);

  const onNamedElementModalClose = useCallback(
    (saved?: boolean) => {
      if (pendingSpecSlug) {
        if (saved) {
          // if saved, then we can finally commit the spec element
          setNamedElement({ type: ElementValueType.Spec, value: pendingSpecSlug });
        }
        // and no matter what, clear the pending state
        setPendingSpecSlug(undefined);
      }
    },
    [pendingSpecSlug, setPendingSpecSlug, setNamedElement],
  );

  const [trie, setTrie] = useState<Pick<Trie<string>, 'put' | 'get' | 'clear'> | undefined>(
    undefined,
  );
  useSelector(getNamedElementsTriePromise).then(t => setTrie(t));

  const canAccess = flags.hasBit(Flag.PermModifyNamedElements);
  const canModifySpec = flags.has(Flag.PermModifySpecMapping);

  const topUnmappedSpecSlugs = useMemo(
    () =>
      topUnmappedSpecObjects
        .filter(object => object.mappableTypes.includes(TargetType.Element))
        .map(object => object.slug),
    [topUnmappedSpecObjects],
  );

  const { promotedOptions, suggestedOptions, idOptions, specOptions, unmappedSpecOptions } =
    useMemo(() => {
      const processedIds = new Set<string>();
      const activeOptions = [
        ...namedElementsById.values(),
        ...suggestedElementsById.values(),
      ].filter(ne => {
        if (!ne.namedElement || !ne.metadata) {
          return false;
        }
        // id comes from two different spots depending on if saved element or suggestion
        const id = (ne.namedElement.id || ne.namedElement.suggestion?.id) ?? '';
        // only want to process the first seen as suggestions still exist after an element is saved
        if (processedIds.has(id)) {
          return false;
        }
        processedIds.add(id);
        // we always keep the selected element, if applicable
        if (namedElement?.type === ElementValueType.Id && id === namedElement.value) {
          return true;
        }
        if (filter && !filter.has(id)) {
          return false;
        }
        // finally, the id based option is acceptable
        // we require it to:
        // 1. not be archived
        // 2. not be a suggestion or be a pending suggestion
        return (
          !ne.metadata.archived &&
          (!!ne.namedElement.id ||
            ne.namedElement.suggestion?.status === Suggestion.Status.STATUS_PENDING)
        );
      });
      const applicableOptions = activeOptions.filter(
        ne => !seen || watchedNamedElementIds.has(ne.namedElement!.id),
      );
      const [_promotedOptions, _allIdOptions] = partition(applicableOptions, ne =>
        promote?.ids?.has(ne.namedElement!.id),
      );
      const promotedOptions: OptionType<ElementValue>[] = _promotedOptions.map(ne => ({
        key: ne.namedElement!.id,
        group: promote?.group,
        value: {
          type: ElementValueType.Id,
          value: ne.namedElement!.id,
        },
        label: ne.namedElement!.name,
      }));
      const [_idOptions, _suggestedOptions] = partition(_allIdOptions, ne => !!ne.namedElement?.id);
      const suggestedOptions: OptionType<ElementValue>[] = _suggestedOptions.map(ne => ({
        key: ne.namedElement!.suggestion!.id,
        group:
          promote && promote.ids.has(ne.namedElement!.suggestion!.id)
            ? promote.group
            : aiSuggestionsGroup,
        value: {
          type: ElementValueType.Id,
          value: ne.namedElement!.suggestion!.id,
        },
        label: ne.namedElement!.name,
        icon: {
          name: 'ai',
          color: 'ai',
          size: 16,
        },
      }));
      const idOptions: OptionType<ElementValue>[] = _idOptions.map(ne => ({
        key: ne.namedElement!.id,
        group: promote && promote.ids.has(ne.namedElement!.id) ? promote.group : allElementsGroup,
        value: {
          type: ElementValueType.Id,
          value: ne.namedElement!.id,
        },
        label: ne.namedElement!.name,
      }));

      promotedOptions.sort((opt1, opt2) => opt1.label.localeCompare(opt2.label));
      suggestedOptions.sort((opt1, opt2) => opt1.label.localeCompare(opt2.label));
      idOptions.sort((opt1, opt2) => opt1.label.localeCompare(opt2.label));

      const mappedSlugs = omitSpecElements
        ? []
        : Object.values(specElementsBySlug)
            .filter(object => object.active && object.mapping)
            .map(object => object.slug);

      const showUnmappedSpec =
        activeOptions.length < UNMAPPED_SPEC_THRESHOLD && canModifySpec && !omitSpecElements;
      const unmappedSpecSlugs = showUnmappedSpec ? topUnmappedSpecSlugs : [];

      const makeSpecOption = (
        objectSlug: string,
        unmapped?: boolean,
      ): OptionType<ElementValue> | undefined => {
        if (!objectSlug) {
          return undefined;
        }

        const specObject = specElementsBySlug[objectSlug];
        if (!specObject) {
          return undefined;
        }

        const group = unmapped
          ? intl.formatMessage(
              {
                defaultMessage: 'Recommended to create: {specName}',
                id: 'U3mhdc',
              },
              { specName: specObject.spec.displayName },
            )
          : specObject.spec.displayName;

        return {
          group,
          key: objectSlug,
          value: {
            type: ElementValueType.Spec,
            value: objectSlug,
          },
          label: getSelectedItemLabel({
            name: specObject.displayName,
            archived: false,
          }),
        };
      };

      const specOptions: OptionType<ElementValue>[] = mappedSlugs
        .map(slug => makeSpecOption(slug))
        .filter(isNonNull);
      // Sort spec options by group, and by label within groups.
      specOptions.sort((opt1, opt2) => {
        const groupOrdering = opt1.group!.localeCompare(opt2.group!);
        if (groupOrdering !== 0) return groupOrdering;
        if (topUnmappedSpecSlugs.includes(opt1.key!)) return 0; // already ordered correctly
        return opt1.label.localeCompare(opt2.label);
      });
      // Unampped spec options are already ordered by priority. We don't want to sort differently.
      const unmappedSpecOptions: OptionType<ElementValue>[] = unmappedSpecSlugs
        .map(slug => makeSpecOption(slug, true))
        .filter(isNonNull);

      return { promotedOptions, suggestedOptions, idOptions, specOptions, unmappedSpecOptions };
    }, [
      namedElement,
      namedElementsById,
      suggestedElementsById,
      seen,
      watchedNamedElementIds,
      intl,
      omitSpecElements,
      specElementsBySlug,
      canModifySpec,
      topUnmappedSpecSlugs,
      promote,
      filter,
    ]);

  const makeSuggestedOptions = useCallback(
    (opts?: {
      requireSelected?: boolean;
      filter?: (option: OptionType<ElementValue>) => boolean;
    }) => {
      const filteredSuggestedOptions = opts?.filter
        ? suggestedOptions.filter(opts.filter)
        : suggestedOptions;
      if (suggestedElementsLimited && filteredSuggestedOptions.length > initialSuggestionsLimit) {
        const limitedSuggestedOptions = filteredSuggestedOptions.slice(
          0,
          initialSuggestionsLimit - 1,
        );
        if (
          opts?.requireSelected &&
          !limitedSuggestedOptions.find(opt => isEqual(opt.value, namedElement))
        ) {
          const opt = filteredSuggestedOptions.find(opt => isEqual(opt.value, namedElement));
          // selected may not be a suggested element, but if it is and we require it, push it onto the end
          if (opt) {
            limitedSuggestedOptions.push(opt);
          }
        }
        return [
          ...limitedSuggestedOptions,
          {
            key: moreSuggestionsKey,
            group: aiSuggestionsGroup,
            value: {
              type: ElementValueType.Id,
              value: moreSuggestionsKey,
            },
            label: 'Show more...',
          },
        ];
      } else {
        return filteredSuggestedOptions;
      }
    },
    [namedElement, suggestedElementsLimited, suggestedOptions],
  );
  const defaultSuggestedOptions = useMemo(
    () => makeSuggestedOptions({ requireSelected: true }),
    [makeSuggestedOptions],
  );

  const options = useMemo(() => {
    if (omitNonPromoted) {
      // we need to handle the case where a given id is selected but for some reason isn't promoted
      // we still want to promote this singular option in the non promoted area for proper functionality
      const idMatchOption = [];
      if (namedElement && !promotedOptions.find(opt => isEqual(opt.value, namedElement))) {
        const idOption = [...defaultSuggestedOptions, ...idOptions].find(opt =>
          isEqual(opt.value, namedElement),
        );
        if (idOption) {
          idMatchOption.push(idOption);
        }
      }
      // only return promoted options plus the optional id match if not part of promoted
      return [...promotedOptions, ...idMatchOption];
    } else {
      // ensure a suggestion is included by default if it's selected
      const selectedSuggestionOption = [];
      if (namedElement?.type === ElementValueType.Id) {
        const selectedSuggestion = defaultSuggestedOptions.find(opt =>
          isEqual(opt.value, namedElement),
        );
        if (selectedSuggestion) {
          selectedSuggestionOption.push(selectedSuggestion);
        }
      }

      return [
        ...promotedOptions,
        // only include selected suggestion by default; other suggestions will be added when filtering
        ...selectedSuggestionOption,
        ...specOptions,
        ...idOptions,
        ...unmappedSpecOptions,
      ];
    }
  }, [
    namedElement,
    promotedOptions,
    defaultSuggestedOptions,
    specOptions,
    idOptions,
    unmappedSpecOptions,
    omitNonPromoted,
  ]);

  const selected = useMemo(
    () => options.find(o => isEqual(o?.value, namedElement)) ?? null,
    [options, namedElement],
  );

  const filterFn = useCallback(
    (items: OptionType<ElementValue>[], filterText: string) => {
      // If the filter text changed, limit the number of suggested elements we'll show again behind the
      // "Show more" option.
      if (filterText !== previousFilterTextRef.current) {
        setSuggestedElementsLimited(true);
      }
      previousFilterTextRef.current = filterText;

      const elements = trie?.get(filterText.toLowerCase()) ?? [];
      const filterNamedElementId = (namedElement: NamedElement) =>
        namedElement.name.toLowerCase().includes(filterText.toLowerCase()) ||
        elements.includes(namedElement.id);

      const filtered = items.filter(item => {
        // Always allow placeholder 'Create new' key if it exists
        if (item.key === CREATE_NEW_KEY) {
          return true;
        }
        // We will add these back later. They are handled a bit differently with "show more" limits.
        if (item.group === aiSuggestionsGroup) {
          return false;
        }

        switch (item.value.type) {
          case ElementValueType.Id: {
            const record =
              namedElementsById.get(item.value.value) ??
              suggestedElementsById.get(item.value.value);
            return record?.namedElement && filterNamedElementId(record.namedElement);
          }
          case ElementValueType.Spec: {
            // If the spec name matches, we're good
            if (item.label.toLowerCase().includes(filterText.toLowerCase())) {
              return true;
            }

            // If the spec name doesn't match, dereference the spec, and see if any of the underlying mappings match
            const elementId = specElementsBySlug[item.value.value]?.mapping?.elementId;
            if (!elementId) {
              return false;
            }

            const namedElementRecord = namedElementsById.get(elementId);
            return (
              namedElementRecord?.namedElement &&
              filterNamedElementId(namedElementRecord.namedElement)
            );
          }
        }
      });

      // Only include suggestions when there is non-empty filter text. This is to prevent suggestions from creating
      // extra noise in select menu options.
      const filteredSuggestions = filterText.trim()
        ? makeSuggestedOptions({
            filter: (item: OptionType<ElementValue>): boolean => {
              const record = suggestedElementsById.get(item.value.value);
              return record?.namedElement && filterNamedElementId(record.namedElement);
            },
          })
        : [];

      return [...filteredSuggestions, ...filtered];
    },
    [
      namedElementsById,
      suggestedElementsById,
      specElementsBySlug,
      trie,
      makeSuggestedOptions,
      setSuggestedElementsLimited,
    ],
  );

  const onChange = useCallback(
    (option: OptionType<ElementValue> | null) => {
      if (option?.key === moreSuggestionsKey) {
        setSuggestedElementsLimited(false);
        return;
      }
      // if an unmapped spec object, we need to open the create modal
      // we don't want to set the element yet
      if (
        option?.value?.type === ElementValueType.Spec &&
        topUnmappedSpecSlugs.includes(option.value.value)
      ) {
        setPendingSpecSlug(option.value.value);
        const newElement = placeholderNamedElementRecord();
        // default the new element name to the name of the spec element
        newElement.namedElement!.name = specElementsBySlug[option.value.value]?.displayName || '';
        setModalInput(newElement);
        return;
      }
      // otherwise, we do handle clearing / setting
      if (clearable) {
        setNamedElement(option?.value || null);
      } else if (option) {
        setNamedElement(option.value);
      }
    },
    [clearable, setNamedElement, setModalInput, specElementsBySlug, topUnmappedSpecSlugs],
  );

  const renderDefinition = useCallback(
    (option: OptionType<ElementValue>) => {
      if (option.key === moreSuggestionsKey) {
        return undefined;
      }
      switch (option.value.type) {
        case ElementValueType.Id: {
          const namedElementRecord = namedElementsById.get(option.value.value);
          const suggestedElementRecord: NamedElementRecord | undefined = suggestedElementsById.get(
            option.value.value,
          );
          const namedOrSuggestedElementRecord = namedElementRecord || suggestedElementRecord;
          return (
            <>
              {namedOrSuggestedElementRecord && (
                <NamedElementBody namedElementRecord={namedOrSuggestedElementRecord} />
              )}
            </>
          );
        }
        case ElementValueType.Spec:
          if (topUnmappedSpecSlugs.includes(option.value.value)) {
            return undefined;
          }
          return <SpecElementBody slug={option.value.value} />;
      }
    },
    [namedElementsById, suggestedElementsById, topUnmappedSpecSlugs],
  );

  const onEdit = useCallback(
    (option: OptionType<ElementValue>) => {
      switch (option.value.type) {
        case ElementValueType.Id: {
          const namedElementRecord = namedElementsById.get(option.value.value);
          const suggestedElementRecord: NamedElementRecord | undefined = suggestedElementsById.get(
            option.value.value,
          );
          const namedOrSuggestedElementRecord = namedElementRecord || suggestedElementRecord;
          if (namedOrSuggestedElementRecord) {
            setModalInput(namedOrSuggestedElementRecord);
          }
          break;
        }
        case ElementValueType.Spec: {
          const elementId = specElementsBySlug[option.value.value]?.mapping?.elementId;
          if (!elementId) {
            return;
          }

          setModalInput(namedElementsById.get(elementId)!);
          break;
        }
      }
    },
    [namedElementsById, suggestedElementsById, setModalInput, specElementsBySlug],
  );

  const getMetadata = useCallback(
    (option: OptionType<ElementValue>): Metadata | null => {
      switch (option.value.type) {
        case ElementValueType.Id: {
          const namedElement = option.value && namedElementsById.get(option.value.value);
          return namedElement && namedElement?.metadata
            ? getNamedElementMetadata(namedElement)
            : null;
        }
        case ElementValueType.Spec: {
          const mapping = specElementsBySlug[option.value.value]?.mapping;
          if (!mapping) {
            return null;
          }
          return toDxObjectsMetadata(mapping.metadata);
        }
      }
    },
    [namedElementsById, specElementsBySlug],
  );

  return (
    <>
      <ResourceSelectMenu<ElementValue>
        preventOverflow={preventOverflow}
        flipY={flipY}
        options={options}
        selected={selected}
        onChange={onChange}
        variant={variant}
        fullWidth={fullWidth}
        compact={compact}
        size={size}
        placeholder={placeholder}
        clearable={clearable}
        itemType="Element"
        renderDefinition={renderDefinition}
        filterFn={filterFn}
        canAccess={canAccess}
        onEdit={onEdit}
        initialOpen={initialOpen}
        onClose={onClose}
        closeOnSelect={closeOnSelect}
        onCreate={onCreate}
        disabled={disabled}
        testId="named-element-select-menu"
        getMetadata={getMetadata}
      />
      {source === NamedElementModalSource.settings &&
        namedElement?.type === ElementValueType.Id &&
        namedElementsById.get(namedElement.value) && (
          <Box marginTop={8}>
            <Button
              size="x-small"
              variant="outline"
              onClick={() => setModalInput(namedElementsById.get(namedElement.value)!)}
            >
              <FormattedMessage defaultMessage="Manage Element" id="njli+J" />
            </Button>
          </Box>
        )}
      {getNamedElementModalComponent({
        initialSpecSlugs: pendingSpecSlug ? [pendingSpecSlug] : undefined,
        onClose: onNamedElementModalClose,
      })}
    </>
  );
};
